import { useEffect, useRef, useState } from "react";

import type { ApiTypes } from "@fluid-app/fluid";
import { getSettings } from "@fluid-app/fluid";

import { waitForSdkReady } from "./sdkInitialization";

// Use type from the API client instead of defining our own
type FluidSettings = ApiTypes["settings-retrieve"]["response"];

// Timeout for settings loading (in milliseconds)
const SETTINGS_LOAD_TIMEOUT = 3000; // 3 seconds

/**
 * Custom hook for accessing Fluid settings in widgets
 *
 * @param options - Configuration options for the hook
 * @param options.skipLoading - If true, the hook will not use a loading state and will return immediately
 * @returns The settings object, loading state, and utility functions
 */
export function useFluidSettings(options: { skipLoading?: boolean } = {}) {
  const { skipLoading = false } = options;
  const [settings, setSettings] = useState<FluidSettings | null>(null);
  const [isLoading, setIsLoading] = useState(!skipLoading);
  const settingsInitialized = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Set up event listener for settings availability
  useEffect(() => {
    // Skip this effect entirely during server-side rendering
    if (typeof window === "undefined") {
      setIsLoading(false);
      return;
    }

    const handleSettingsAvailable = () => {
      const settingsData = getSettings();
      if (settingsData) {
        setSettings(settingsData);
        if (!skipLoading) {
          setIsLoading(false);

          // Clear timeout since we got settings
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        }
      }
    };

    // Set up event listener
    const initialize = async () => {
      try {
        if (settingsInitialized.current) return;
        settingsInitialized.current = true;

        await waitForSdkReady();

        // Check if settings already exist
        const existingSettings = getSettings();
        if (existingSettings) {
          setSettings(existingSettings);
          if (!skipLoading) {
            setIsLoading(false);
          }
        } else if (!skipLoading) {
          // Settings don't exist yet, but SDK is ready
          // Keep loading state true until settings are available (only if not skipping loading)
          setIsLoading(true);

          // Set a timeout to prevent infinite loading
          timeoutRef.current = setTimeout(() => {
            console.warn(
              "Settings load timeout reached, proceeding without settings",
            );
            setIsLoading(false);
            timeoutRef.current = null;
          }, SETTINGS_LOAD_TIMEOUT);
        }

        // Add event listener for future settings updates
        window.addEventListener("SETTINGS_AVAILABLE", handleSettingsAvailable);
      } catch (error) {
        console.warn("Could not initialize settings:", error);
        // Even if there's an error, stop loading to prevent infinite loading state
        if (!skipLoading) {
          setIsLoading(false);
        }
      }
    };

    void initialize();

    // Clean up event listener and timeout
    return () => {
      window.removeEventListener("SETTINGS_AVAILABLE", handleSettingsAvailable);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [skipLoading]);

  // Helper to apply CSS variables to shadow DOM
  const applyCompanyColorToShadowDOM = (
    shadowRoot: ShadowRoot | null,
    colorValue?: string,
  ) => {
    if (shadowRoot && colorValue) {
      // Set company color CSS variable on the shadow root host
      const host = shadowRoot.host as HTMLElement;

      // Ensure colorValue has proper format - only prepend # if needed
      const formattedColor = colorValue.startsWith("#")
        ? colorValue
        : `#${colorValue}`;
      host.style.setProperty("--company-color", formattedColor);
    }
  };

  // Get effective display names based on props and settings
  const getDisplayNames = (
    propShopName?: string,
    propAffiliateImage?: string,
    propLogoSrc?: string,
  ) => {
    // Shop display name prioritizes prop, then settings, then default
    const shopDisplayName =
      propShopName ?? settings?.settings?.shop?.name ?? "Fluid";

    // Get affiliate name from settings
    const affiliateDisplayName = settings?.settings?.affiliate?.name ?? "";

    // Get logo and avatar sources with fallbacks
    // Priority: 1. Props passed directly, 2. Affiliate data, 3. Shop data
    const logoSrc = propLogoSrc ?? settings?.settings?.shop?.logo_url ?? "";

    // Generate affiliate initials if affiliate exists but has no image
    const shouldUseAffiliateInitials =
      affiliateDisplayName &&
      !propAffiliateImage &&
      !settings?.settings?.affiliate?.image_url;

    // For avatar: 1. Props, 2. Affiliate data, 3. Shop data (but only if no affiliate initials)
    const avatarSrc =
      propAffiliateImage ??
      settings?.settings?.affiliate?.image_url ??
      (shouldUseAffiliateInitials ? "" : settings?.settings?.shop?.logo_url) ??
      "";

    // Get primary color from settings
    const primaryColor = settings?.settings?.shop?.primary_color;

    const affiliateInitials = shouldUseAffiliateInitials
      ? affiliateDisplayName
          .split(" ")
          .map((name) => name.charAt(0).toUpperCase())
          .slice(0, 2) // Only take first 2 initials
          .join("")
      : "";

    return {
      shopDisplayName,
      affiliateDisplayName,
      logoSrc,
      avatarSrc,
      primaryColor,
      shouldUseAffiliateInitials,
      affiliateInitials,
    };
  };

  return {
    settings,
    isLoading,
    applyCompanyColorToShadowDOM,
    getDisplayNames,
  };
}
